import {Meta} from '@rdcomponents/meta/meta';
import {Tabs} from 'antd';
import {Tab} from 'rc-tabs/lib/interface';
import {useMemo} from 'react';

import {appRoute} from '../../../app-route';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../layout/page/page';
import {useDomainConfig} from '../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../provider/domain-config/domain-config-type';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../service/analytics/analytics';
import {NeverError} from '../../../util/error';

import {NotificationBrowser} from './notification-browser/notification-browser';
import {NotificationEmail} from './notification-email/notification-email';
import {NotificationTabWrapper} from './notification-tab-wrapper/notification-tab-wrapper';
import {NotificationTelegramOrMax} from './notification-telegram/notification-telegram-or-max';
import {NotificationTabKeyEnum} from './notification-type';
import {TelegramAnalytics} from './telegram-analytics/telegram-analytics';

const NOTIFICATION_TAB_WRAPPER_TYPE_BROWSER = 'push';
const NOTIFICATION_TAB_WRAPPER_TYPE_EMAIL = 'email';
const NOTIFICATION_TAB_WRAPPER_TYPE_TELEGRAM = 'telegram';

function getNotificationTab(key: NotificationTabKeyEnum, isRocketDataMessengerNotificationsTab: boolean): Tab {
    switch (key) {
        case NotificationTabKeyEnum.email:
            return {
                key: NotificationTabKeyEnum.email,
                label: <Locale stringKey="NOTIFICATIONS__TAB_INFO__EMAIL_NOTIFICATIONS" />,
                children: (
                    <NotificationTabWrapper type={NOTIFICATION_TAB_WRAPPER_TYPE_EMAIL}>
                        <NotificationEmail />
                    </NotificationTabWrapper>
                ),
            };
        case NotificationTabKeyEnum.browser:
            return {
                key: NotificationTabKeyEnum.browser,
                label: <Locale stringKey="NOTIFICATIONS__TAB_INFO__BROWSER_NOTIFICATIONS" />,
                children: (
                    <NotificationTabWrapper type={NOTIFICATION_TAB_WRAPPER_TYPE_BROWSER}>
                        <NotificationBrowser />
                    </NotificationTabWrapper>
                ),
            };
        case NotificationTabKeyEnum.telegram:
            return {
                key: NotificationTabKeyEnum.telegram,
                label: (
                    <Locale
                        stringKey={
                            isRocketDataMessengerNotificationsTab
                                ? 'NOTIFICATIONS__TAB_INFO__TELEGRAM_MAX_NOTIFICATIONS'
                                : 'NOTIFICATIONS__TAB_INFO__TELEGRAM_NOTIFICATIONS'
                        }
                    />
                ),
                children: (
                    <NotificationTabWrapper type={NOTIFICATION_TAB_WRAPPER_TYPE_TELEGRAM}>
                        <NotificationTelegramOrMax />
                    </NotificationTabWrapper>
                ),
            };
        case NotificationTabKeyEnum.telegramAnalytics:
            return {
                key: NotificationTabKeyEnum.telegramAnalytics,
                label: <Locale stringKey="NOTIFICATIONS__TAB_INFO__TELEGRAM_ANALYTICS" />,
                children: <TelegramAnalytics />,
            };
        default: {
            throw new NeverError(key);
        }
    }
}

function handleTabChange(value: string) {
    // eslint-disable-next-line default-case
    switch (value) {
        case NotificationTabKeyEnum.browser:
            track(AnalyticsTarget.Settings.Notification.BrowserTabClick);
            break;
        case NotificationTabKeyEnum.telegram:
            track(AnalyticsTarget.Settings.Notification.TelegramTabClick);
            break;
        case NotificationTabKeyEnum.email:
            track(AnalyticsTarget.Settings.Notification.EmailTabClick);
            break;
        case NotificationTabKeyEnum.telegramAnalytics:
            break;
    }
}

export function Notification(): JSX.Element {
    const {getLocalizedString} = useLocale();

    const {domainName, notificationTabs} = useDomainConfig();
    const isRocketDataMessengerNotificationsTab = domainName === DomainNameEnum.rocketData;

    const tabItems = useMemo(
        () => notificationTabs.map((key) => getNotificationTab(key, isRocketDataMessengerNotificationsTab)),
        [notificationTabs, isRocketDataMessengerNotificationsTab]
    );

    return (
        <Page isTopFilterHidden>
            <Meta title={getLocalizedString('CATEGORY_NAME__NOTIFICATIONS')} />

            <BreadCrumbs
                list={[
                    {
                        path: appRoute.settingsCompaniesGroup.path,
                        titleLangKey: 'CATEGORY_NAME__NOTIFICATIONS',
                    },
                ]}
            />

            <Tabs defaultActiveKey="email-notifications" items={tabItems} onChange={handleTabChange} />
        </Page>
    );
}
